import { CliError, CliErrorCode } from '@wix/cli-error';
import { extname } from 'node:path';
import type { build } from 'vite';
import { normalizeGlobbyPattern } from '../dev-center/components/path.js';

export type ViteBuildOutput = Awaited<ReturnType<typeof build>>;

function extractBuildOutputs(buildOutput: ViteBuildOutput) {
  const isValidRollupOutput =
    'output' in buildOutput || Array.isArray(buildOutput);

  if (!isValidRollupOutput) {
    throw new CliError({
      code: CliErrorCode.InvalidViteBuildOutput(),
      cause: null,
    });
  }

  const outputs = Array.isArray(buildOutput)
    ? buildOutput.flatMap((output) => output.output)
    : buildOutput.output;

  return outputs;
}

function extractPresetAssetOutput(
  buildOutput: ViteBuildOutput,
  presetAssetFileName: string
) {
  const outputs = extractBuildOutputs(buildOutput);

  const presetFile = outputs.find((output) => {
    return (
      output.type === 'asset' && output.names.includes(presetAssetFileName)
    );
  });

  if (!presetFile) {
    throw new CliError({
      code: CliErrorCode.ViteFileNotFoundInOutput({
        fileName: presetAssetFileName,
      }),
      cause: null,
    });
  }

  return presetFile;
}

function extractAssetOutput(buildOutput: ViteBuildOutput, fileName: string) {
  const outputs = extractBuildOutputs(buildOutput);

  return outputs.find((output) => {
    return (
      output.type === 'asset' &&
      output.originalFileNames.some(
        (originalFileName) =>
          normalizeGlobbyPattern(fileName) === originalFileName
      ) &&
      // Workaround for the fact that some assets with different file extensions are outputted with the same originalFileName
      extname(fileName) === extname(output.fileName)
    );
  });
}

export function extractBundleAssetFileName(
  buildOutput: ViteBuildOutput,
  fileName: string
) {
  const output = extractAssetOutput(buildOutput, fileName);

  if (!output) {
    throw new CliError({
      code: CliErrorCode.ViteModuleBundleNotFound(),
      cause: null,
    });
  }

  return output.fileName;
}

function extractModuleBundleChunk(
  buildOutput: ViteBuildOutput,
  moduleId: string
) {
  const outputs = extractBuildOutputs(buildOutput);

  const output = outputs
    .filter((output) => output.type === 'chunk')
    .find((output) => output.facadeModuleId === moduleId);

  if (!output) {
    throw new CliError({
      code: CliErrorCode.ViteModuleBundleNotFound(),
      cause: null,
    });
  }

  return output;
}

export function extractModuleBundleName(
  buildOutput: ViteBuildOutput,
  moduleId: string
) {
  return extractModuleBundleChunk(buildOutput, moduleId).fileName;
}

export function extractModuleBundleCss(
  buildOutput: ViteBuildOutput,
  moduleId: string
) {
  const output = extractModuleBundleChunk(buildOutput, moduleId);
  const importedCss = output.viteMetadata?.importedCss;

  return importedCss?.values().next().value;
}

export function extractPresetCss(
  buildOutput: ViteBuildOutput,
  presetAssetFileName: string
) {
  const output = extractPresetAssetOutput(buildOutput, presetAssetFileName);
  return output.fileName;
}

export function extractModuleBundleContent(
  buildOutput: ViteBuildOutput,
  fileName: string
) {
  const output = extractAssetOutput(buildOutput, fileName);

  if (!output || !('source' in output)) {
    throw new CliError({
      code: CliErrorCode.ViteFileNotFoundInOutput({ fileName }),
      cause: null,
    });
  }

  return output.source.toString();
}
